import { useState, useEffect, useCallback } from 'react';
import { 
  Popup, 
  PopupCategory, 
  PopupQuery, 
  PopupResponse, 
  PopupStats,
  PopupInteraction,
  getPopups,
  getActivePopups,
  getPopupById,
  createPopup,
  updatePopup,
  deletePopup,
  getPopupCategories,
  createPopupCategory,
  updatePopupCategory,
  deletePopupCategory,
  recordPopupInteraction,
  getPopupStats,
  shouldShowPopup
} from '../utils/supabase';

// Hook for managing popups in admin panel
export const usePopups = () => {
  const [popups, setPopups] = useState<Popup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchPopups = useCallback(async (query: PopupQuery = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response: PopupResponse = await getPopups(query);
      setPopups(response.popups);
      setTotalPages(response.total_pages);
      setTotal(response.total);
    } catch (err) {
      console.error('Error fetching popups:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch popups');
    } finally {
      setLoading(false);
    }
  }, []);

  const createNewPopup = useCallback(async (popupData: Omit<Popup, 'id' | 'created_at' | 'updated_at' | 'view_count' | 'click_count' | 'conversion_rate' | 'current_displays'>) => {
    try {
      setError(null);
      const newPopup = await createPopup(popupData);
      setPopups(prev => [newPopup, ...prev]);
      return newPopup;
    } catch (err) {
      console.error('Error creating popup:', err);
      setError(err instanceof Error ? err.message : 'Failed to create popup');
      throw err;
    }
  }, []);

  const updateExistingPopup = useCallback(async (id: string, updates: Partial<Popup>) => {
    try {
      setError(null);
      const updatedPopup = await updatePopup(id, updates);
      setPopups(prev => prev.map(popup => popup.id === id ? updatedPopup : popup));
      return updatedPopup;
    } catch (err) {
      console.error('Error updating popup:', err);
      setError(err instanceof Error ? err.message : 'Failed to update popup');
      throw err;
    }
  }, []);

  const deleteExistingPopup = useCallback(async (id: string) => {
    try {
      setError(null);
      await deletePopup(id);
      setPopups(prev => prev.filter(popup => popup.id !== id));
    } catch (err) {
      console.error('Error deleting popup:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete popup');
      throw err;
    }
  }, []);

  const getPopupDetails = useCallback(async (id: string) => {
    try {
      setError(null);
      return await getPopupById(id);
    } catch (err) {
      console.error('Error fetching popup details:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch popup details');
      throw err;
    }
  }, []);

  return {
    popups,
    loading,
    error,
    totalPages,
    total,
    fetchPopups,
    createNewPopup,
    updateExistingPopup,
    deleteExistingPopup,
    getPopupDetails
  };
};

// Hook for managing popup categories
export const usePopupCategories = () => {
  const [categories, setCategories] = useState<PopupCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async (activeOnly = true) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPopupCategories(activeOnly);
      setCategories(data);
    } catch (err) {
      console.error('Error fetching popup categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  }, []);

  const createNewCategory = useCallback(async (categoryData: Omit<PopupCategory, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      setError(null);
      const newCategory = await createPopupCategory(categoryData);
      setCategories(prev => [...prev, newCategory]);
      return newCategory;
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err instanceof Error ? err.message : 'Failed to create category');
      throw err;
    }
  }, []);

  const updateExistingCategory = useCallback(async (id: string, updates: Partial<PopupCategory>) => {
    try {
      setError(null);
      const updatedCategory = await updatePopupCategory(id, updates);
      setCategories(prev => prev.map(cat => cat.id === id ? updatedCategory : cat));
      return updatedCategory;
    } catch (err) {
      console.error('Error updating category:', err);
      setError(err instanceof Error ? err.message : 'Failed to update category');
      throw err;
    }
  }, []);

  const deleteExistingCategory = useCallback(async (id: string) => {
    try {
      setError(null);
      await deletePopupCategory(id);
      setCategories(prev => prev.filter(cat => cat.id !== id));
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete category');
      throw err;
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    loading,
    error,
    fetchCategories,
    createNewCategory,
    updateExistingCategory,
    deleteExistingCategory
  };
};

// Hook for displaying active popups on frontend
export const useActivePopups = (currentPath?: string) => {
  const [activePopups, setActivePopups] = useState<Popup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionId] = useState(() => {
    // Generate or get session ID from localStorage
    let sessionId = localStorage.getItem('popup_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('popup_session_id', sessionId);
    }
    return sessionId;
  });

  const fetchActivePopups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const popups = await getActivePopups(currentPath);
      
      // Filter popups based on frequency rules and user session
      const filteredPopups = [];
      for (const popup of popups) {
        const shouldShow = await shouldShowPopup(popup, sessionId);
        if (shouldShow) {
          filteredPopups.push(popup);
        }
      }
      
      setActivePopups(filteredPopups);
    } catch (err) {
      console.error('Error fetching active popups:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch popups');
    } finally {
      setLoading(false);
    }
  }, [currentPath, sessionId]);

  const recordInteraction = useCallback(async (
    popupId: string, 
    interactionType: 'viewed' | 'clicked' | 'closed' | 'dismissed',
    additionalData?: any
  ) => {
    try {
      const interaction: Omit<PopupInteraction, 'id' | 'created_at'> = {
        popup_id: popupId,
        session_id: sessionId,
        user_ip: '', // Will be handled by backend if needed
        user_agent: navigator.userAgent,
        interaction_type: interactionType,
        page_url: window.location.href,
        interaction_data: additionalData
      };

      await recordPopupInteraction(interaction);

      // Remove popup from active list if it was closed/dismissed and frequency is once_per_session
      if ((interactionType === 'closed' || interactionType === 'dismissed')) {
        setActivePopups(prev => {
          const popup = prev.find(p => p.id === popupId);
          if (popup && (popup.show_frequency === 'once_per_session' || popup.show_frequency === 'once_per_day')) {
            return prev.filter(p => p.id !== popupId);
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('Error recording popup interaction:', err);
    }
  }, [sessionId]);

  const dismissPopup = useCallback((popupId: string) => {
    recordInteraction(popupId, 'dismissed');
  }, [recordInteraction]);

  const clickPopup = useCallback((popupId: string, additionalData?: any) => {
    recordInteraction(popupId, 'clicked', additionalData);
  }, [recordInteraction]);

  useEffect(() => {
    fetchActivePopups();
  }, [fetchActivePopups]);

  return {
    activePopups,
    loading,
    error,
    sessionId,
    recordInteraction,
    dismissPopup,
    clickPopup,
    refetch: fetchActivePopups
  };
};

// Hook for popup statistics
export const usePopupStats = () => {
  const [stats, setStats] = useState<PopupStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPopupStats();
      setStats(data);
    } catch (err) {
      console.error('Error fetching popup stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats
  };
};

// Hook for popup display logic with timing and positioning
export const usePopupDisplay = () => {
  const [displayedPopups, setDisplayedPopups] = useState<Set<string>>(new Set());
  const [popupQueue, setPopupQueue] = useState<Popup[]>([]);

  const addToQueue = useCallback((popup: Popup) => {
    setPopupQueue(prev => {
      // Avoid duplicates and sort by priority
      const filtered = prev.filter(p => p.id !== popup.id);
      return [...filtered, popup].sort((a, b) => (b.priority || 0) - (a.priority || 0));
    });
  }, []);

  const removeFromQueue = useCallback((popupId: string) => {
    setPopupQueue(prev => prev.filter(p => p.id !== popupId));
    setDisplayedPopups(prev => {
      const newSet = new Set(prev);
      newSet.delete(popupId);
      return newSet;
    });
  }, []);

  const markAsDisplayed = useCallback((popupId: string) => {
    setDisplayedPopups(prev => new Set([...Array.from(prev), popupId]));
  }, []);

  const getNextPopup = useCallback(() => {
    return popupQueue.find(popup => !displayedPopups.has(popup.id || ''));
  }, [popupQueue, displayedPopups]);

  const clearQueue = useCallback(() => {
    setPopupQueue([]);
    setDisplayedPopups(new Set());
  }, []);

  return {
    displayedPopups,
    popupQueue,
    addToQueue,
    removeFromQueue,
    markAsDisplayed,
    getNextPopup,
    clearQueue
  };
};